import { Container, Sprite, Texture } from 'pixi.js'
import { MAX_MERGED_SPARK_VALUE, SPARK_SIZE } from '@star-snake/shared'
import { NeonColorsHex } from '../ui/neon-theme'

interface SparkData {
  id: string
  x: number
  z: number
  value: number
  color: number
  hidden?: boolean
}

interface SparkClusterLobe {
  x: number
  y: number
}

interface SparkTextureVariant {
  texture: Texture
  diameter: number
  variantIndex: number
}

interface SparkViewport {
  x: number
  z: number
  width: number
  height: number
}

const SPARK_COLORS = [
  NeonColorsHex.gold,
  NeonColorsHex.primary,
  NeonColorsHex.success,
  NeonColorsHex.info,
]
const QUALITY_SPARK_CAP = [512, 1024, 2048, 4096]
const BASE_SPARK_DIAMETER = SPARK_SIZE * 4
const SPARK_TEXTURE_PIXEL_SIZE = 6
const SPARK_CORE_RADIUS_CELLS = 2
const SPARK_GLOW_RADIUS_CELLS = 4
const SPARK_PADDING_CELLS = 4
const SPARK_CLUSTER_LAYOUTS: ReadonlyArray<ReadonlyArray<ReadonlyArray<SparkClusterLobe>>> = [
  [[{ x: 0, y: 0 }]],
  [
    [{ x: -2.2, y: 0 }, { x: 2.2, y: 0 }],
    [{ x: -2.0, y: -0.8 }, { x: 2.0, y: 0.8 }],
    [{ x: -1.9, y: 0.9 }, { x: 1.9, y: -0.9 }],
  ],
  [
    [{ x: 0, y: -2.3 }, { x: -2.0, y: 1.8 }, { x: 2.0, y: 1.8 }],
    [{ x: -2.2, y: -1.8 }, { x: 2.2, y: -1.8 }, { x: 0, y: 2.1 }],
    [{ x: -2.5, y: 0 }, { x: 0, y: -2.2 }, { x: 2.5, y: 0.2 }],
    [{ x: -2.4, y: -0.2 }, { x: 0.3, y: 2.1 }, { x: 2.2, y: -1.7 }],
    [{ x: -1.8, y: -2.0 }, { x: -2.0, y: 1.6 }, { x: 2.4, y: 0.5 }],
    [{ x: -2.1, y: -1.3 }, { x: 2.3, y: -0.9 }, { x: 0, y: 2.4 }],
  ],
] as const
const SPARK_VIEW_MARGIN = 320
const SPARK_TWINKLE_BASE_ALPHA = 0.95
const SPARK_TWINKLE_AMPLITUDE = 0.035
const SPARK_TWINKLE_SPEED = 2.4

export class SparkRenderer {
  private container: Container
  private sparkSprites: Map<string, Sprite> = new Map()
  private readonly sparkSpritePool: Sprite[] = []
  private sparkPhaseCache: Map<string, number> = new Map()
  private sparkTextureKeyCache: Map<string, number> = new Map()
  private pulseTime = 0
  private readonly sparkTextures: SparkTextureVariant[][]
  private readonly activeIds: Set<string> = new Set()
  private readonly presentIds: Set<string> = new Set()
  private qualityLevel = 2
  onSparkCollected: ((x: number, z: number) => void) | null = null
  onSelfSparkCollected: ((value: number) => void) | null = null
  private predictedCollected: Set<string> = new Set()
  private predictedCollectedValues: Map<string, number> = new Map()

  constructor(parentContainer: Container) {
    this.container = new Container()
    parentContainer.addChild(this.container)
    this.sparkTextures = this.createSparkTextures()
  }

  update(sparks: Iterable<SparkData>, dt: number, viewport?: SparkViewport): void {
    this.pulseTime += dt
    this.activeIds.clear()
    this.presentIds.clear()
    const capIdx = Math.max(0, Math.min(QUALITY_SPARK_CAP.length - 1, this.qualityLevel))
    const maxVisible = QUALITY_SPARK_CAP[capIdx]
    let visibleCount = 0
    const minX = viewport ? viewport.x - SPARK_VIEW_MARGIN : Number.NEGATIVE_INFINITY
    const minY = viewport ? viewport.z - SPARK_VIEW_MARGIN : Number.NEGATIVE_INFINITY
    const maxX = viewport ? viewport.x + viewport.width + SPARK_VIEW_MARGIN : Number.POSITIVE_INFINITY
    const maxY = viewport ? viewport.z + viewport.height + SPARK_VIEW_MARGIN : Number.POSITIVE_INFINITY

    for (const spark of sparks) {
      this.presentIds.add(spark.id)
      let sprite = this.sparkSprites.get(spark.id)

      if (spark.hidden) {
        if (sprite) sprite.visible = false
        this.predictedCollected.add(spark.id)
        this.predictedCollectedValues.set(spark.id, this.clampSparkValue(spark.value))
        continue
      }

      this.predictedCollected.delete(spark.id)
      this.predictedCollectedValues.delete(spark.id)

      const inViewport =
        spark.x >= minX &&
        spark.x <= maxX &&
        spark.z >= minY &&
        spark.z <= maxY
      if (!inViewport) {
        if (sprite) sprite.visible = false
        continue
      }

      if (visibleCount >= maxVisible) {
        if (sprite) sprite.visible = false
        continue
      }
      visibleCount++
      this.activeIds.add(spark.id)

      const sparkValue = this.clampSparkValue(spark.value)
      const variant = this.getSparkVariant(spark.id, sparkValue)
      const textureKey = sparkValue * 10 + variant.variantIndex
      if (!sprite) {
        sprite = this.acquireSparkSprite(spark.color, variant)
        this.container.addChild(sprite)
        this.sparkSprites.set(spark.id, sprite)
        this.sparkTextureKeyCache.set(spark.id, textureKey)
      } else if (this.sparkTextureKeyCache.get(spark.id) !== textureKey) {
        sprite.texture = variant.texture
        sprite.width = variant.diameter
        sprite.height = variant.diameter
        this.sparkTextureKeyCache.set(spark.id, textureKey)
      }

      sprite.visible = true
      sprite.tint = SPARK_COLORS[spark.color % SPARK_COLORS.length]

      let sparkPhase = this.sparkPhaseCache.get(spark.id)
      if (sparkPhase === undefined) {
        sparkPhase = (this.hashSparkId(spark.id) % 1024) / 1024 * Math.PI * 2
        this.sparkPhaseCache.set(spark.id, sparkPhase)
      }

      sprite.x = spark.x
      sprite.y = spark.z
      sprite.alpha = this.qualityLevel === 0
        ? 0.98
        : SPARK_TWINKLE_BASE_ALPHA + Math.sin(this.pulseTime * SPARK_TWINKLE_SPEED + sparkPhase) * SPARK_TWINKLE_AMPLITUDE
    }

    for (const [id, sprite] of this.sparkSprites) {
      if (!this.presentIds.has(id)) {
        if (this.predictedCollected.has(id) && this.onSelfSparkCollected) {
          this.onSelfSparkCollected(this.predictedCollectedValues.get(id) ?? 1)
        }
        this.predictedCollected.delete(id)
        this.predictedCollectedValues.delete(id)
        if (this.onSparkCollected) {
          this.onSparkCollected(sprite.x, sprite.y)
        }
        this.container.removeChild(sprite)
        this.releaseSparkSprite(sprite)
        this.sparkSprites.delete(id)
        this.sparkPhaseCache.delete(id)
        this.sparkTextureKeyCache.delete(id)
        continue
      }
      if (!this.activeIds.has(id)) sprite.visible = false
    }
  }

  private clampSparkValue(value: number): number {
    return Math.max(1, Math.min(MAX_MERGED_SPARK_VALUE, Math.floor(value)))
  }

  private createSparkSprite(colorIndex: number, variant: SparkTextureVariant): Sprite {
    const sprite = new Sprite(variant.texture)
    sprite.anchor.set(0.5)
    sprite.tint = SPARK_COLORS[colorIndex % SPARK_COLORS.length]
    sprite.alpha = 0.98
    sprite.width = variant.diameter
    sprite.height = variant.diameter
    return sprite
  }

  private acquireSparkSprite(colorIndex: number, variant: SparkTextureVariant): Sprite {
    const sprite = this.sparkSpritePool.pop() ?? this.createSparkSprite(colorIndex, variant)
    sprite.texture = variant.texture
    sprite.tint = SPARK_COLORS[colorIndex % SPARK_COLORS.length]
    sprite.alpha = 0.98
    sprite.visible = true
    sprite.width = variant.diameter
    sprite.height = variant.diameter
    sprite.rotation = 0
    return sprite
  }

  private releaseSparkSprite(sprite: Sprite): void {
    sprite.visible = false
    this.sparkSpritePool.push(sprite)
  }

  private createSparkTextures(): SparkTextureVariant[][] {
    const gridSizes = SPARK_CLUSTER_LAYOUTS.map((variants) => {
      let maxGridSize = 0
      for (const layout of variants) {
        maxGridSize = Math.max(maxGridSize, this.getClusterGridSize(layout))
      }
      return maxGridSize
    })
    const baseGridSize = Math.max(1, gridSizes[0] ?? 1)

    return SPARK_CLUSTER_LAYOUTS.map((variants, index) => {
      const diameter = BASE_SPARK_DIAMETER * (gridSizes[index] / baseGridSize)
      return variants.map((layout, variantIndex) => ({
        texture: this.createSparkTexture(layout, gridSizes[index]),
        diameter,
        variantIndex,
      }))
    })
  }

  private getClusterGridSize(lobes: readonly SparkClusterLobe[]): number {
    const maxOffset = lobes.reduce((max, lobe) => Math.max(max, Math.abs(lobe.x), Math.abs(lobe.y)), 0)
    const gridRadius = Math.ceil(maxOffset + SPARK_GLOW_RADIUS_CELLS + SPARK_PADDING_CELLS)
    return gridRadius * 2 + 1
  }

  private getSparkVariant(sparkId: string, value: number): SparkTextureVariant {
    const variants = this.sparkTextures[Math.max(0, Math.min(this.sparkTextures.length - 1, value - 1))]
    const variantIndex = this.hashSparkId(sparkId) % variants.length
    return variants[variantIndex]
  }

  private hashSparkId(sparkId: string): number {
    let hash = 2166136261
    for (let i = 0; i < sparkId.length; i++) {
      hash ^= sparkId.charCodeAt(i)
      hash = Math.imul(hash, 16777619)
    }
    return hash >>> 0
  }

  private createSparkTexture(lobes: readonly SparkClusterLobe[], gridSize: number): Texture {
    const pixelSize = SPARK_TEXTURE_PIXEL_SIZE
    const size = gridSize * pixelSize
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) return Texture.WHITE

    ctx.imageSmoothingEnabled = false
    const centerCell = (gridSize - 1) / 2
    for (const lobe of lobes) {
      this.drawSparkLobe(
        ctx,
        centerCell + lobe.x,
        centerCell + lobe.y,
        pixelSize,
        SPARK_CORE_RADIUS_CELLS,
        SPARK_GLOW_RADIUS_CELLS,
      )
    }

    return Texture.from(canvas)
  }

  private drawSparkLobe(
    ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    pixelSize: number,
    coreRadius: number,
    glowRadius: number,
  ): void {
    const minX = Math.floor(centerX - glowRadius)
    const maxX = Math.ceil(centerX + glowRadius)
    const minY = Math.floor(centerY - glowRadius)
    const maxY = Math.ceil(centerY + glowRadius)

    for (let gy = minY; gy <= maxY; gy++) {
      for (let gx = minX; gx <= maxX; gx++) {
        const dx = gx - centerX
        const dy = gy - centerY
        const distance = Math.hypot(dx, dy)
        if (distance > glowRadius + 0.15) continue

        let alpha = 0
        if (distance <= coreRadius) {
          alpha = 0.5 + (1 - distance / Math.max(0.001, coreRadius)) * 0.45
        } else {
          alpha = 0.08 + (1 - (distance - coreRadius) / Math.max(0.001, glowRadius - coreRadius)) * 0.24
        }

        ctx.fillStyle = `rgba(255,255,255,${Math.max(0, Math.min(1, alpha))})`
        ctx.fillRect(gx * pixelSize, gy * pixelSize, pixelSize, pixelSize)
      }
    }

    const coreX = Math.round(centerX) * pixelSize
    const coreY = Math.round(centerY) * pixelSize
    ctx.fillStyle = 'rgba(255,255,255,0.98)'
    ctx.fillRect(coreX, coreY, pixelSize, pixelSize)
    ctx.fillStyle = 'rgba(255,255,255,0.78)'
    ctx.fillRect(coreX - pixelSize, coreY - pixelSize, pixelSize, pixelSize)
  }

  dispose(): void {
    for (const sprite of this.sparkSprites.values()) {
      sprite.destroy()
    }
    for (const sprite of this.sparkSpritePool) {
      sprite.destroy()
    }
    this.container.removeChildren()
    this.sparkSprites.clear()
    this.sparkSpritePool.length = 0
    this.sparkPhaseCache.clear()
    this.sparkTextureKeyCache.clear()
    this.predictedCollected.clear()
    this.predictedCollectedValues.clear()
    for (const variants of this.sparkTextures) {
      for (const variant of variants) {
        variant.texture.destroy(true)
      }
    }
  }

  setQualityLevel(level: number): void {
    if (!Number.isFinite(level)) return
    this.qualityLevel = Math.max(0, Math.min(3, Math.floor(level)))
  }

  getActiveSpriteCount(): number {
    return this.sparkSprites.size
  }

  copyActiveSparkIdsTo(target: string[]): string[] {
    target.length = 0
    for (const sparkId of this.activeIds) {
      target.push(sparkId)
    }
    return target
  }
}
